import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { History, Upload, Edit, Trash2, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HistoryPage = () => {
  const { projectId } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [projectId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/history`);
      setHistory(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <Plus className="w-5 h-5 text-green-600" />;
      case 'update':
        return <Edit className="w-5 h-5 text-blue-600" />;
      case 'delete':
        return <Trash2 className="w-5 h-5 text-red-600" />;
      case 'import':
        return <Upload className="w-5 h-5 text-purple-600" />;
      default:
        return <History className="w-5 h-5 text-slate-600" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'create':
        return 'Crear';
      case 'update':
        return 'Actualizar';
      case 'delete':
        return 'Eliminar';
      case 'import':
        return 'Importar';
      default:
        return action;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      case 'import':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Cargando historial...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-2" data-testid="history-title">Historial de Cambios</h1>
        <p className="text-sm text-slate-600">Registro de todas las modificaciones realizadas</p>
      </div>

      {history.length === 0 ? (
        <Card className="p-8">
          <div className="text-center">
            <History className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">No hay cambios registrados aún</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {history.map((entry) => (
            <Card key={entry.id} className="p-6" data-testid={`history-entry-${entry.id}`}>
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  {getActionIcon(entry.action)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(entry.action)}`}>
                      {getActionLabel(entry.action)}
                    </span>
                    {entry.file_type && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {entry.file_type}.txt
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-slate-600 mb-1">
                    <span className="font-medium text-slate-800">{entry.user_name}</span>
                    {entry.action === 'import' && entry.changes?.imported_files && (
                      <span> importó {entry.changes.imported_files.length} archivos</span>
                    )}
                    {entry.action === 'create' && ' creó un nuevo registro'}
                    {entry.action === 'update' && ' actualizó un registro'}
                    {entry.action === 'delete' && ' eliminó un registro'}
                  </p>

                  <p className="text-xs text-slate-500">
                    {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true, locale: es })}
                  </p>

                  {/* Additional details */}
                  {entry.changes && entry.action === 'import' && entry.changes.imported_files && (
                    <div className="mt-2 text-xs text-slate-600">
                      <p>Archivos importados:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {entry.changes.imported_files.map((file, index) => (
                          <span key={index} className="px-2 py-0.5 rounded bg-slate-100 font-mono">
                            {file}.txt
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <div className="text-xs text-slate-500 flex-shrink-0">
                  {new Date(entry.timestamp).toLocaleString('es-ES', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
